import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, Leaf } from "lucide-react";
import { NAV_LINKS, BUSINESS } from "@/data/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-[var(--border)] bg-[var(--bg)]/85 backdrop-blur-xl py-3"
          : "bg-transparent py-5"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 md:px-12">
        <button data-testid="nav-logo" onClick={() => go("#home")} className="flex items-center gap-2.5 text-left">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--green)] text-white">
            <Leaf size={20} />
          </span>
          <span className="leading-tight">
            <span className={`block font-display text-xl font-bold tracking-tight ${scrolled ? "text-[var(--ink)]" : "text-white"}`}>
              M N Savanur
            </span>
            <span className={`overline text-[0.6rem] ${scrolled ? "text-[var(--green)]" : "text-[var(--gold)]"}`}>
              Wholesale Merchants
            </span>
          </span>
        </button>

        <div className="hidden items-center gap-8 lg:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => go(l.href)}
              className={`link-underline text-sm font-medium transition-colors ${
                scrolled ? "text-[var(--ink-soft)] hover:text-[var(--green)]" : "text-white/90 hover:text-white"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${BUSINESS.phone}`}
            data-testid="nav-call-now"
            className="group hidden items-center gap-2 rounded-full bg-[var(--gold)] px-5 py-2.5 text-sm font-semibold text-[#1a1a1a] shadow-sm transition-all duration-300 hover:shadow-lg hover:shadow-yellow-700/20 sm:flex"
          >
            <Phone size={16} className="transition-transform group-hover:rotate-12" />
            Call Now
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className={`lg:hidden ${scrolled || open ? "text-[var(--ink)]" : "text-white"}`}
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="overflow-hidden border-t border-[var(--border)] bg-[var(--bg)] lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-${l.label.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => go(l.href)}
                  className="rounded-lg py-3 text-left text-base font-medium text-[var(--ink)] hover:text-[var(--green)]"
                >
                  {l.label}
                </button>
              ))}
              <a href={`tel:${BUSINESS.phone}`} className="mt-2 flex items-center justify-center gap-2 rounded-full bg-[var(--gold)] py-3 font-semibold text-[#1a1a1a]">
                <Phone size={16} /> Call {BUSINESS.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
